import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { DashboardSidebar } from "./DashboardSidebar";
import { DashboardTopbar } from "./DashboardTopbar";
import { AdminSectionNav } from "./AdminSectionNav";
import { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

/**
 * Smartlead-inspired dashboard chrome:
 *  - Full-width dark navy topbar with white wordmark & actions
 *  - White sidebar beneath it, grouped menus with light tinted active state
 *  - Light gray canvas, white panels
 *  - Brand accent stays blue
 */
export function DashboardShell({ children }: { children: ReactNode }) {
  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "16rem",
          "--sidebar-width-icon": "3.5rem",
        } as React.CSSProperties
      }
    >
      <div className="min-h-screen flex flex-col w-full bg-background">
        <DashboardTopbar />
        <div className="flex flex-1 w-full min-h-0">
          <DashboardSidebar />
          <SidebarInset className="flex-1 flex flex-col min-w-0 bg-background border-l border-border">
            <AdminSectionNav />
            <main className="flex-1 min-w-0">{children}</main>
          </SidebarInset>
        </div>
      </div>
    </SidebarProvider>
  );
}

/**
 * Sticky page header — pinned under the topbar (h-14). Sits on the light
 * canvas with a white card-like band underneath via Tabs/Body composition.
 */
export function StickyPageHeader({
  title,
  subtitle,
  icon,
  backTo,
  backLabel = "Back",
  actions,
  tabs,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  backTo?: string;
  backLabel?: string;
  actions?: ReactNode;
  tabs?: ReactNode;
}) {
  return (
    <div className="bg-background border-b border-border">
      <div className="max-w-[1400px] mx-auto px-6 pt-4 pb-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {backTo && (
            <Link
              to={backTo}
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground mb-1"
            >
              <ArrowLeft className="h-3 w-3" /> {backLabel}
            </Link>
          )}
          <h1 className="text-[20px] font-bold tracking-tight flex items-center gap-2 leading-tight">
            {icon}
            {title}
          </h1>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 flex-wrap">{actions}</div>
        )}
      </div>
      {tabs && (
        <div className="max-w-[1400px] mx-auto px-6 overflow-x-auto">{tabs}</div>
      )}
    </div>
  );
}

export function PageBody({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`max-w-[1400px] mx-auto px-6 py-6 ${className}`}>{children}</div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
      <div className="min-w-0">
        <h1 className="text-[20px] font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}
